import type {
  WorkerFleet, SourceHealthOverview,
  ConnectionHistory, ConnectionQuery, ConfigurationOverview,
} from '../types/observatory'
import type { ScheduleLoad, ScheduleLoadQuery } from '../types/scheduleLoad'
import { fetchApi } from './api'

/**
 * Observatory operational data: the live readers for the workers, sources,
 * connections, configuration and schedule-load pages.
 *
 * THERE IS NO MOCK LAYER. Every function below calls the endpoint named in its
 * comment through `fetchApi`, with the same auth and error handling as every
 * other call. The fixture functions that used to sit here -- the Overview's
 * KPIs, attention list, live operations, health, throughput, top entities,
 * recent executions, signal inbox, governance queue and report registry -- are
 * deleted. Every Overview panel now reads a live endpoint through its own
 * service, except the signal inbox, which has no store behind it (W6) and says so.
 *
 * ETL runs moved to ./etlRunsApi.ts, alongside their types in ../types/etlRuns.ts.
 */

// ---------------------------------------------------------------------------
// GET /api/observatory/workers          LIVE
//
// Served by ObservatoryWorkersController from Etl.WorkerHeartbeat, which
// Automaton projects out of Hangfire's live server list every 15 seconds.
// Liveness, per-server utilization and the fleet aggregate are all derived
// server-side; nothing here is inferred or invented.
// ---------------------------------------------------------------------------
export function getWorkerFleet(): Promise<WorkerFleet> {
  return fetchApi<WorkerFleet>('/api/observatory/workers')
}

// ---------------------------------------------------------------------------
// GET /api/observatory/sources          LIVE
//
// Served by ObservatorySourcesController from Etl.SourceConnectionHealth, which
// Automaton's SourceHealthWorker publishes every 60 seconds -- one row per
// registered ISourceConnectionProbe per host. The API holds no Oracle package
// (D14) and so cannot dial a source; it reports what the worker's connectors
// observed.
//
// EVERYTHING that speaks in the present tense is derived server-side at read
// time and must not be recomputed here: reachability (a strict-precedence
// ladder where a stale projection outranks every other signal), status,
// statusLabel, detail, isUsable, isPermanentFault, and the three header counts.
// Each of those has a recorded bug behind it.
//
// This 500s until migration AddSourceConnectionHealth is applied -- migrations
// are applied by hand, never by the app -- so the error branch is the first
// thing this surface shows on a fresh machine.
// ---------------------------------------------------------------------------
export function getSourceHealth(): Promise<SourceHealthOverview> {
  return fetchApi<SourceHealthOverview>('/api/observatory/sources')
}

// ---------------------------------------------------------------------------
// GET /api/observatory/connections      LIVE
//
// Served by ObservatoryConnectionsController from Etl.EtlRunChunkAttempt --
// append-only, thirty days, newest first, paged and filtered server-side.
//
// THE APPEND-ONLY COUNTERPART to getSourceHealth above, and not a richer version
// of it: that one is a single row per source overwritten every 60 seconds, this
// one overwrites nothing. Current state versus history.
//
// EVERY FILTER IS APPLIED SERVER-SIDE, and must stay that way. The table reaches
// ~6,300 rows over thirty days and the endpoint caps a page at 200, so a client
// that filtered a page it had already fetched would be filtering a window of the
// answer and calling it the answer.
//
// An unknown `outcome` is refused with a 400 rather than ignored -- a filter
// silently dropped returns a full list that looks like an answer to the question
// that was asked. The SPA only ever sends values from EtlConnectionOutcome, so
// that 400 is a wire-contract break rather than a user error.
//
// This 500s until migration AddEtlRunChunkAttempts is applied -- migrations are
// applied by hand, never by the app.
// ---------------------------------------------------------------------------
export function getConnections(query: ConnectionQuery = {}): Promise<ConnectionHistory> {
  const params = new URLSearchParams()

  // Only set parameters that were actually asked for. An empty string is a value
  // to the model binder, not an absent filter -- `entityName=` would match rows
  // whose entity is the empty string, of which there are none, so a blank text
  // box would silently return nothing rather than everything.
  if (query.skip != null) params.set('skip', String(query.skip))
  if (query.top != null) params.set('top', String(query.top))
  if (query.entityName) params.set('entityName', query.entityName)
  if (query.module) params.set('module', query.module)
  if (query.runId) params.set('runId', query.runId)
  if (query.outcome) params.set('outcome', query.outcome)
  if (query.from) params.set('from', query.from)
  if (query.to) params.set('to', query.to)

  const qs = params.toString()
  return fetchApi<ConnectionHistory>(`/api/observatory/connections${qs ? `?${qs}` : ''}`)
}

// ---------------------------------------------------------------------------
// GET /api/observatory/configuration    LIVE, read-only
//
// Served by ObservatoryConfigurationController: the API's own allowlisted
// settings from its startup snapshot, plus Automaton's from
// Etl.HostConfiguration, which HostConfigurationWorker publishes every ten
// minutes. Per host, because configuration here is per host.
//
// `LighthouseAdmin` at class level, so outside Development this 403s for anyone
// without the Lighthouse.Admin role — which, until that role is provisioned in
// Entra, is everyone. In Development the policy degrades to any authenticated
// user and the checked-in dev API key satisfies it.
//
// There is no write counterpart and there must not be one in this version (doc
// 16 §9). This 500s until migration AddHostConfiguration is applied -- and the
// vite proxy answers 500 when the API is simply not running, which lands here
// identically.
// ---------------------------------------------------------------------------
export function getHostConfiguration(): Promise<ConfigurationOverview> {
  return fetchApi<ConfigurationOverview>('/api/observatory/configuration')
}

// ---------------------------------------------------------------------------
// GET /api/observatory/schedule-load?zone=&hours=    LIVE, read-only
//
// Served by ObservatoryScheduleLoadController: a forecast of worker-slot demand
// from scheduled reports and scheduled jobs against the live slot count, with
// every figure behind every verdict. Its contract is in ../types/scheduleLoad.ts.
//
// `zone` is REQUIRED and there is no default on either side. The API answers a
// missing or unrecognised zone with a 400 rather than drawing the forecast in
// UTC, and this client never substitutes one either: a forecast drawn in a zone
// nobody asked for puts every spike in the wrong hour, which is doc 11 §6.1's
// whole warning. `hours` outside 1..168 is also a 400 -- refused, not clamped.
//
// `LighthouseAdmin` at class level (it reads every schedule in every workspace),
// so outside Development this 403s without the Lighthouse.Admin role. It 500s
// until migration AddSchedulingZoneAndQueueBacklog is applied, because the
// controller reads the new columns -- and the vite proxy answers 500 when the
// API is simply not running, which lands here identically.
// ---------------------------------------------------------------------------
export function getScheduleLoad(query: ScheduleLoadQuery): Promise<ScheduleLoad> {
  const params = new URLSearchParams({ zone: query.zone, hours: String(query.hours) })
  return fetchApi<ScheduleLoad>(`/api/observatory/schedule-load?${params.toString()}`)
}
